import { create } from "zustand";
import {
  fetchProducts,
  fetchProductById,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../services/productService";
import { Product, ProductFilters, ProductPayload } from "../types";

interface ProductStore {
  products: Product[];
  total: number;
  loading: boolean;
  error: string | null;
  currentProduct: Product | null;
  filters: ProductFilters;

  fetchAllProducts: () => Promise<void>;
  fetchProduct: (id: number) => Promise<void>;
  setFilter: <K extends keyof ProductFilters>(key: K, value: ProductFilters[K]) => void;
  resetFilters: () => void;

  addProduct: (productData: ProductPayload) => Promise<void>;
  editProduct: (id: number, productData: Product) => Promise<void>;
  removeProduct: (id: number) => Promise<void>;
}

export const useProductStore = create<ProductStore>((set, get) => ({
  products: [],
  total: 0,
  loading: false,
  error: null,
  currentProduct: null,
  filters: {
    sort_by: "created_at",
    order: "desc",
    limit: 12,
    offset: 0,
  },

  fetchAllProducts: async () => {
    set({ loading: true, error: null });
    try {
      const { filters } = get();
      const { products, total } = await fetchProducts(filters);
      set({ products, total, loading: false });
    } catch (err) {
      console.error("Ошибка загрузки продуктов:", err);
      set({ error: "Ошибка загрузки продуктов", loading: false });
    }
  },

  fetchProduct: async (id) => {
    set({ loading: true, error: null });
    try {
      const product = await fetchProductById(id);
      set({ currentProduct: product, loading: false });
    } catch (err) {
      console.error(`Ошибка загрузки продукта с ID ${id}:`, err);
      set({ error: `Ошибка загрузки продукта с ID ${id}`, loading: false });
    }
  },

  setFilter: (key, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [key]: value,
      },
    })),

  resetFilters: () =>
    set(() => ({
      filters: {
        sort_by: "created_at",
        order: "desc",
        limit: 12,
        offset: 0,
      },
    })),

  addProduct: async (productData) => {
    try {
      const { product } = await createProduct(productData);
      set((state) => ({ products: [...state.products, product] }));
    } catch (err) {
      console.error("Ошибка создания продукта:", err);
    }
  },

  editProduct: async (id, productData) => {
    try {
      await updateProduct(id, productData);
      set((state) => ({
        products: state.products.map((product) =>
          product.id === id ? { ...product, ...productData } : product
        ),
      }));
    } catch (err) {
      console.error(`Ошибка обновления продукта с ID ${id}:`, err);
    }
  },

  removeProduct: async (id) => {
    try {
      await deleteProduct(id);
      set((state) => ({
        products: state.products.filter((product) => product.id !== id),
      }));
    } catch (err) {
      console.error(`Ошибка удаления продукта с ID ${id}:`, err);
    }
  },
}));
